import FilterItem from '../FilterItem';
import FilterListStorage from './FilterListStorage';
import { featureOptionsToItems } from './featureValues';

class FilterConditionGroup extends FilterItem {
  valuesStorage = null;
  #allValuesPreselected = false;
  #hasFullValuesList = false;

  constructor(conf, subcategory) {
    super(conf, subcategory);
    this.valuesStorage = new FilterListStorage();
    if (this.isFeature && conf?.valuesList?.length) {
      this.valuesStorage.setValuesList(featureOptionsToItems(conf.valuesList));
      this.#hasFullValuesList = true;
    }
    this.#buildGroups();
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  setValuesList(valuesList) {
    this.valuesStorage.setValuesList(valuesList);
  }

  /**
   * Stores the complete option list (as opposed to the label-only subset loaded in `apply`).
   * @param {Array} valuesList
   */
  setFullValuesList(valuesList) {
    this.valuesStorage.setValuesList(valuesList);
    this.#hasFullValuesList = true;
  }

  get valuesList() {
    return this.valuesStorage.valuesList;
  }

  get hasFullValuesList() {
    return this.#hasFullValuesList;
  }

  getValueLabel = (id) => this.valuesStorage.getValueLabel(id);

  get selectAllOnActivate() {
    return this.options.selectAllOnActivate || false;
  }

  get hasSelectedValues() {
    return this.groups.some((group) => normalizeGroupValues(group?.items).length > 0);
  }

  /**
   * Fills the filter with every available option once, right after activation.
   * Values restored from the URL or a saved user filter are never overwritten,
   * and a manual clean-up is not undone until the filter is removed and added again.
   * @returns {boolean} whether the values were preselected
   */
  preselectAllValues() {
    if (!this.selectAllOnActivate || this.#allValuesPreselected || this.na || this.hasSelectedValues) {
      return false;
    }

    const values = this.valuesList
      .map((item) => this.valuesStorage.getItemValue(item))
      .filter((value) => !isBlankValue(value));
    if (!values.length) {
      return false;
    }

    this.#allValuesPreselected = true;
    if (!this.groups.length) {
      this.addGroup();
    }
    this.setGroupValues(this.groups[0].id, values);
    return true;
  }

  resetValue = () => {
    this.#allValuesPreselected = false;
    this.#buildGroups();
  };

  async apply(groups, valuesMap, rawValue) {
    // Feature N/A hydration: a raw entry like `{ [featureID]: null, not: true }`
    // means "no value". Restore it as the N/A state instead of an empty NOT group,
    // otherwise it collapses to nothing and the filter loses its N/A on reload.
    if (this.isFeature && this.useNASearch && this.#isFeatureNAValue(rawValue)) {
      this.na = true;
      this.#buildGroups();
      this.valuesStorage.setValuesList(await this.#fetchItems([]));
      this.#hasFullValuesList = this.#hasStaticFeatureOptions();
      return;
    }

    const normalizedGroups = (groups || []).map((group) => this.#normalizeGroup(group));

    this.groups = [];
    for (const group of normalizedGroups) {
      if (group === false) {
        this.na = true;
        continue;
      }
      const result = this.#makeGroup(group);
      if (this.#isGroupArray(result)) {
        this.groups.push(...result);
      } else {
        this.groups.push(result);
      }
    }

    const items = await this.#fetchItems(normalizedGroups);
    this.valuesStorage.setValuesList(items);
    // Hydration loads only what is needed to label the selected values (and without the
    // subcategory scope), so the form must still fetch the complete option list.
    this.#hasFullValuesList = this.#hasStaticFeatureOptions();
  }

  collectValues() {
    if (this.isFeature) {
      return { __feature: this.#collectFeatures() };
    }
    if (this.isSocialMedia) {
      return { [this.property]: JSON.stringify(this.#collectProductTypeValues()) };
    }
    return this.#gatherValues();
  }

  getConditionsGroups(value) {
    return (value || [])
      .map((group) => ({
        notValue: group.notValue,
        labels: (group.items || [])
          .map((item) => this.valuesStorage.getValueLabel(item.value))
          .filter((label) => label !== 'id:'),
      }))
      .filter((group) => group.labels.length > 0);
  }

  getConditionsString(value) {
    return this.getConditionsGroups(value)
      .map((group) => `${group.notValue ? ' NOT ' : ''}(${group.labels.join(' OR ')})`)
      .join(' AND ');
  }

  addGroup() {
    const newGroup = this.#makeGroup();
    if (this.#isGroupArray(newGroup)) {
      this.groups.push(...newGroup);
    } else {
      this.groups.push(newGroup);
    }
  }

  removeGroup(groupID) {
    if (this.groups.length <= 1) return;
    const index = this.#findGroupIndexByID(groupID);
    if (index !== -1) {
      this.groups.splice(index, 1);
    }
  }

  getGroupNOTValue(groupID) {
    const groupIndex = this.#findGroupIndexByID(groupID);
    if (groupIndex < 0) return false;
    return this.groups[groupIndex]?.notValue ?? false;
  }

  changeGroupNOTValue(groupID, value) {
    const groupIndex = this.#findGroupIndexByID(groupID);
    if (groupIndex !== -1) {
      this.groups[groupIndex].notValue = value;
    }
  }

  setGroupValues(groupID, values = []) {
    const groupIndex = this.#findGroupIndexByID(groupID);
    if (groupIndex === -1) return;

    const normalizedValues = (Array.isArray(values) ? values : [values]).filter((value) => !isBlankValue(value));

    this.groups[groupIndex].items = normalizedValues.length
      ? normalizedValues.map((value) => ({ id: this.#makeID(), value }))
      : [{ id: this.#makeID(), value: '' }];
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  #buildGroups() {
    const initialGroup = this.#makeGroup();
    if (this.#isGroupArray(initialGroup)) {
      this.value = initialGroup;
      this.groups = [...initialGroup];
    } else {
      this.value = [initialGroup];
      this.groups = [initialGroup];
    }
  }

  #normalizeGroup(group) {
    if (group === false) return false;
    if (isBlankValue(group)) return [];

    if (this.isSocialMedia) {
      if (typeof group === 'string') return group;
      if (Array.isArray(group) && group.length === 1 && typeof group[0] === 'string') return group[0];
    }

    if (Array.isArray(group)) return group;
    return [group];
  }

  #hasStaticFeatureOptions() {
    return this.isFeature && typeof this.fieldConf?.items !== 'function';
  }

  async #fetchItems(normalizedGroups) {
    if (this.#hasStaticFeatureOptions()) {
      return featureOptionsToItems(this.options?.valuesList);
    }
    if (this.id === 'and_group_or_types') {
      const selectedTypeIds = [
        ...new Set(
          normalizedGroups
            .filter(Array.isArray)
            .flat()
            .filter((value) => !isBlankValue(value) && !isNotToken(value))
        ),
      ];
      const params = { id: selectedTypeIds };
      if (this.subCat != null) params.categories = this.subCat;
      return this.fieldConf?.items(params);
    }
    return this.fieldConf?.items();
  }

  #isFeatureNAValue(rawValue) {
    const entries = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
    if (!entries.length) return false;
    return entries.every((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      if (entry.not !== true) return false;
      const valueKey = Object.keys(entry).find((key) => key !== 'not');
      return valueKey != null && (entry[valueKey] === null || entry[valueKey] === undefined);
    });
  }

  #isGroupArray(result) {
    return Array.isArray(result) && result.length > 0 && result[0]?.id != null;
  }

  #collectProductTypeValues() {
    return this.groups.reduce((filter, group) => {
      const values = normalizeGroupValues(group?.items || []);
      if (values.length) {
        filter.push({ not: group.notValue, group: values });
      }
      return filter;
    }, []);
  }

  #gatherValues() {
    if (this.na) {
      const prop = this.options.naProp || this.property;
      return { [prop]: false };
    }
    const filter = {};
    let i = 0;
    for (const group of this.groups) {
      const values = normalizeGroupValues(group.items || []);
      if (!values.length) continue;

      const prop = `${this.property}[${i + 1}]`;
      filter[prop] = group.notValue ? [...values, 'not'] : values;
      i++;
    }
    return filter;
  }

  #collectFeatures() {
    const { featureID } = this;
    if (this.isNA) return { [featureID]: null, not: true };

    return this.groups.reduce((features, group) => {
      const values = group.items.map(({ value }) => value).filter(Boolean);
      if (values.length) {
        const entry = { [featureID]: values };
        if (group.notValue) entry.not = true;
        features.push(entry);
      }
      return features;
    }, []);
  }

  /** Parses a JSON-encoded group string for social-media-style properties. */
  #parseJsonGroup(values) {
    const filter = JSON.parse(values);
    return filter.map((item) => ({
      notValue: item.not,
      id: this.#makeID(),
      items: item.group.map((value) => ({ id: this.#makeID(), value })),
    }));
  }

  #makeGroup(values = []) {
    const isJsonProperty =
      this.property === 'product_types.id' ||
      this.property === 'applications.id' ||
      this.property === 'ingredients.id' ||
      this.property === 'effects.id' ||
      this.property === 'new_launches.id' ||
      this.property === 'region';

    if (isJsonProperty && typeof values === 'string') {
      return this.#parseJsonGroup(values);
    }

    const normalized = Array.isArray(values) ? values : isBlankValue(values) ? [] : [values];

    if (!normalized.length) {
      return { notValue: false, id: this.#makeID(), items: [{ id: this.#makeID(), value: '' }] };
    }

    const items = normalized.map((value) => ({ id: this.#makeID(), value }));
    const notIndex = items.findIndex((el) => isNotToken(el.value));
    const notValue = notIndex >= 0;
    if (notValue) items.splice(notIndex, 1);

    return { notValue, id: this.#makeID(), items };
  }

  #makeID() {
    return Math.floor(Date.now() * Math.random());
  }

  #findGroupIndexByID(id) {
    return this.groups.findIndex((item) => item.id === id);
  }
}

// ─── Module-level helpers ────────────────────────────────────────────────────

function normalizeGroupValues(items = []) {
  return items.map((item) => item?.value).filter((value) => !isBlankValue(value));
}

function isBlankValue(value) {
  return value == null || String(value).trim() === '';
}

function isNotToken(value) {
  return String(value).toLowerCase() === 'not';
}

export default FilterConditionGroup;
